import { addYear, monthEnd, monthStart, yearEnd, yearStart } from "@formkit/tempo";

/**
 * The windows behind the chips on Afgerond.
 *
 * Each one is a calendar range and nothing more. Keeping "only what is already worked" out of here
 * is deliberate: that is a property of a shift, not of a month, and the screens that use these
 * ranges apply it themselves. It used to be smuggled into `this-year` by ending the range at "now"
 * — which papered over the same gap in `this-month`, where the default chip happily included shifts
 * still to come.
 *
 * Kanttekening: alle grenzen hier komen uit de kalender van het toestel, niet uit de projecttijd.
 * Voor een medewerker in Nederland is dat hetzelfde; wie in een andere zone staat, ziet de grenzen
 * van "deze maand" met zijn eigen offset meeschuiven. Dat rechtzetten betekent de hele module op
 * projecttijd zetten — grenzen én de datum die erin gaat — en dat is een aparte ingreep.
 */
export const PERIOD_SELECT_OPTIONS = [
  {
    label: "modals:period-select.options.this-month",
    value: "this-month",
    // `monthEnd` keeps the current time of day, so the bare version cut the last day of the month
    // off at whatever time you happened to be looking.
    filter: (date) => ({ from: monthStart(date), to: endOfLocalDay(monthEnd(date)) }),
  },
  {
    label: "modals:period-select.options.this-year",
    value: "this-year",
    filter: (date) => ({ from: yearStart(date), to: endOfLocalDay(yearEnd(date)) }),
  },
  {
    label: "modals:period-select.options.last-year",
    value: "last-year",
    filter: (date) => {
      const lastYear = addYear(date, -1);
      // Tot en met het laatste moment van 31 december, anders valt oudejaarsavond eruit.
      return { from: yearStart(lastYear), to: endOfLocalDay(yearEnd(lastYear)) };
    },
  },
  {
    label: "modals:period-select.options.all-years",
    value: "all-years",
    filter: (date) => ({ from: new Date(0), to: endOfLocalDay(date) }),
  },
] as const satisfies ReadonlyArray<{
  label: string;
  value: string;
  filter: (date: Date) => { from: Date; to: Date };
}>;

export type PeriodSelectOption = (typeof PERIOD_SELECT_OPTIONS)[number]["value"];

/**
 * Het laatste moment van de dag waarop `date` valt.
 *
 * Stond er als tempo's `dayEnd`, dat sindsdien verboden is omdat het zonder tijdzone rekent. Hier
 * is dat geen fout maar de bedoeling: `monthStart`/`monthEnd`/`yearStart`/`yearEnd` hierboven
 * werken óók in de kalender van het toestel, en een einde-van-de-dag in projecttijd tussen grenzen
 * in toesteltijd zou de reeks juist scheef trekken — zie de kanttekening bij
 * `PERIOD_SELECT_OPTIONS`.
 */
function endOfLocalDay(date: Date): Date {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end;
}
